import './RouteInputs.scss'

import { HDSIcon } from '@here/hds-react-components'
import CloseButton from 'components/App/components/CloseButton'
import { RouteWaypoint } from 'data/model/routePlanner'
import { i18n } from 'lib/i18n'
import React, { useCallback, useMemo, useState } from 'react'
import { DragDropContext, DragStart, Droppable, DropResult } from 'react-beautiful-dnd'

import { RouteWaypointInput } from './RouteWaypointInput'

export interface WaypointInput {
  id: string
  index: number
  wayPoint: RouteWaypoint
}

export enum OperationType {
  add,
  remove,
  reorder,
}

export enum WaypointType {
  source,
  stop,
  destination,
}

interface RouteInputsProps {
  routeWaypoints: RouteWaypoint[]
  isMyLocationShared: boolean
  disabledStops: boolean
  showAddStopBtn: boolean
  disabled?: boolean
  focusIndex?: number
  onEnterKeyDown?: () => void
  onCloseButtonClick?: () => void
  onFocus?: (index: number, value: string) => void
  onRouteInputChange?: (value: string) => void
  onRouteInputsBlur?: () => void
  onRouteWaypointChange?: (wayPoints: RouteWaypoint[], operation?: OperationType) => void
}

function createWaypointInput(index: number, waypoint?: RouteWaypoint): WaypointInput {
  return { id: `waypoint-${index}`, index: index, wayPoint: waypoint ?? ({} as RouteWaypoint) }
}

export const RouteInputs = ({
  routeWaypoints,
  isMyLocationShared,
  disabledStops,
  showAddStopBtn,
  focusIndex = -1,
  onCloseButtonClick,
  onEnterKeyDown: onInputEnterKeyDown,
  onFocus,
  onRouteInputChange,
  onRouteWaypointChange,
  onRouteInputsBlur,
}: RouteInputsProps): JSX.Element => {
  const i18nBaseKey = 'route-planner.'
  const addStopButtonText = i18n(`${i18nBaseKey}add-stop-input-placeholder`)
  const waypointInputs: WaypointInput[] = useMemo(() => {
    // If there is no waypoints - we show two empty fields.
    // If there is one waypoints- we show the source and empty destination.
    // Otherwise, we show all
    return routeWaypoints.length === 0
      ? [createWaypointInput(0), createWaypointInput(1)]
      : routeWaypoints.length === 1
      ? [createWaypointInput(0, routeWaypoints[0]), createWaypointInput(1)]
      : routeWaypoints.map((waypoint, index) => createWaypointInput(index, waypoint))
  }, [routeWaypoints])

  const sourceInput = waypointInputs[0]
  const destinationInput = waypointInputs[waypointInputs.length - 1]
  const stopInputs = waypointInputs.slice(1, -1)

  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showDragIcon, setShowDragIcon] = useState(-1)

  const showReverse = Boolean(
    [sourceInput, ...stopInputs, destinationInput].find(
      (input) => input.wayPoint?.place?.length > 0
    )
  )

  const handleInputKeyDown = useCallback(
    (e: CustomEvent | KeyboardEvent): void => {
      if (!(e instanceof KeyboardEvent)) {
        return
      }
      if (e.key !== 'Enter') {
        return
      }

      onInputEnterKeyDown && onInputEnterKeyDown()
    },
    [onInputEnterKeyDown]
  )

  const handleRouteInputChange = useCallback(
    (stopInput: WaypointInput): void => {
      const value = stopInput.wayPoint.place
      onRouteInputChange && onRouteInputChange(value)
    },
    [onRouteInputChange]
  )

  const handleRouteInputFocus = useCallback(
    (waypointInput: WaypointInput): void => {
      onFocus && onFocus(waypointInput.index, waypointInput.wayPoint.place)
    },
    [onFocus]
  )

  // TODO: Need to handle blur events with document click handler as hds-input fires these events twice
  const handleRouteInputsBlur = useCallback((): void => {
    onRouteInputsBlur && onRouteInputsBlur()
  }, [onRouteInputsBlur])

  const handleReverse = useCallback((): void => {
    const newWaypoints = [...routeWaypoints]
    if (newWaypoints.length === 1) {
      newWaypoints.push({} as RouteWaypoint)
    }
    onRouteWaypointChange && onRouteWaypointChange(newWaypoints.reverse(), OperationType.reorder)
  }, [routeWaypoints, onRouteWaypointChange])

  const addStopClick = useCallback(() => {
    !disabledStops &&
      onRouteWaypointChange &&
      onRouteWaypointChange([...routeWaypoints, {} as RouteWaypoint], OperationType.add)
  }, [disabledStops, routeWaypoints, onRouteWaypointChange])

  const removeStopClick = useCallback(
    (index: number) => {
      const newWaypoints = [...routeWaypoints]
      newWaypoints.splice(index, 1)
      onRouteWaypointChange && onRouteWaypointChange(newWaypoints, OperationType.remove)
    },
    [routeWaypoints, onRouteWaypointChange]
  )

  const onDragStart = ({ draggableId }: DragStart): void => {
    const draggedInput = waypointInputs.find((input) => input.id === draggableId)
    draggedInput && setShowDragIcon(draggedInput.index)
  }

  const onDragEnd = useCallback(
    ({ destination, source, draggableId }: DropResult): void => {
      setShowDragIcon(-1)
      if (!destination) {
        return
      }

      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return
      }
      const draggable = waypointInputs.find((w) => w.id === draggableId) ?? ({} as WaypointInput)
      waypointInputs.splice(source.index, 1)
      waypointInputs.splice(destination.index, 0, draggable)
      const newWypointInputs = waypointInputs.map((waypointInput, index) => {
        return { ...waypointInput, order: index }
      })
      onRouteWaypointChange &&
        onRouteWaypointChange(
          newWypointInputs.map((pt) => pt.wayPoint ?? ({} as RouteWaypoint)),
          OperationType.reorder
        )
    },
    [waypointInputs, onRouteWaypointChange]
  )

  return (
    <div className={showAddStopBtn ? 'RouteInputs' : 'RouteInputs RouteInputs--padding'}>
      <div className="RouteInputs__container">
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div
                className="RouteInputs__droppable"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                <RouteWaypointInput
                  showDragIcon={showDragIcon}
                  isFocused={focusIndex === sourceInput.index}
                  waypointInput={sourceInput}
                  inputColor={getInputColor(isMyLocationShared, sourceInput)}
                  waypointType={WaypointType.source}
                  isDraggable={disabledStops}
                  onBlur={handleRouteInputsBlur}
                  onChange={handleRouteInputChange}
                  onEnterKeyDown={handleInputKeyDown}
                  onFocus={handleRouteInputFocus}
                >
                  {showReverse && focusIndex !== 0 ? (
                    <HDSIcon
                      slot="action"
                      name="switch-vertical"
                      iconStyle="solid"
                      size="16px"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleReverse()
                      }}
                    />
                  ) : undefined}
                </RouteWaypointInput>

                {stopInputs?.length > 0 &&
                  (!isCollapsed ? (
                    stopInputs.map((stop) => (
                      <RouteWaypointInput
                        isFocused={focusIndex === stop.index}
                        showDragIcon={showDragIcon}
                        key={stop.id}
                        waypointInput={stop}
                        inputColor={getInputColor(isMyLocationShared, stop)}
                        waypointType={WaypointType.stop}
                        isDraggable={disabledStops}
                        onEnterKeyDown={handleInputKeyDown}
                        onBlur={handleRouteInputsBlur}
                        onChange={handleRouteInputChange}
                        onFocus={handleRouteInputFocus}
                      />
                    ))
                  ) : (
                    <div className="RouteInputs__stops-collapsed-container">
                      <span
                        className="RouteInputs__stops-collapsed"
                        onClick={() => setIsCollapsed((prevState) => !prevState)}
                      >
                        {stopInputs.length}
                      </span>
                    </div>
                  ))}

                <RouteWaypointInput
                  waypointInput={destinationInput}
                  showDragIcon={showDragIcon}
                  isFocused={focusIndex === destinationInput.index}
                  inputColor={getInputColor(isMyLocationShared, destinationInput)}
                  waypointType={WaypointType.destination}
                  isDraggable={disabledStops}
                  onEnterKeyDown={handleInputKeyDown}
                  onBlur={handleRouteInputsBlur}
                  onChange={handleRouteInputChange}
                  onFocus={handleRouteInputFocus}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="RouteInputs__IconListContainer">
          {focusIndex < 0 ? (
            <div className="RouteInputs__card-close-button-container">
              <CloseButton onClick={() => onCloseButtonClick && onCloseButtonClick()} />
            </div>
          ) : stopInputs?.length > 0 && focusIndex === 0 ? (
            <HDSIcon
              className="RouteInputs__icon"
              name="substract"
              category="core-ui"
              onMouseDown={() => removeStopClick(0)}
            />
          ) : (
            <div className="RouteInputs__icon-empty"></div>
          )}

          {stopInputs?.length > 0 &&
            !isCollapsed &&
            stopInputs.map((stop, index) =>
              focusIndex === stop.index ? (
                <HDSIcon
                  key={index}
                  className="RouteInputs__icon"
                  name="substract"
                  category="core-ui"
                  onMouseDown={() => removeStopClick(stop.index)}
                />
              ) : (
                <div key={index} className="RouteInputs__icon-empty"></div>
              )
            )}

          {stopInputs?.length > 0 && focusIndex < 0 ? (
            <HDSIcon
              className="RouteInputs__icon"
              name={isCollapsed ? 'expand' : 'collapse'}
              category="core-ui"
              onClick={() => setIsCollapsed((prevState) => !prevState)}
            />
          ) : stopInputs?.length > 0 && focusIndex === waypointInputs.length - 1 ? (
            <HDSIcon
              className="RouteInputs__icon"
              name="substract"
              category="core-ui"
              onMouseDown={() => removeStopClick(waypointInputs.length - 1)}
            />
          ) : (
            <div className="RouteInputs__icon-empty"></div>
          )}
        </div>
      </div>

      {showAddStopBtn && (
        <div className="RouteInputs__card-buttons-container">
          <span
            className={`RouteInputs__AddButton_link ' 
                ${disabledStops ? 'disabled' : 'enabled'}`}
            onClick={addStopClick}
          >
            <HDSIcon name="add" category="core-ui" />
            <span className="RouteInputs__AddButton_text">{addStopButtonText}</span>
          </span>
        </div>
      )}
    </div>
  )
}

function getInputColor(
  isMyLocationShared: boolean | undefined,
  waypointInput: WaypointInput
): string | undefined {
  const currentLocationLabel = i18n('route-planner.current-location-input-placeholder')
  if (
    isMyLocationShared === true &&
    waypointInput.wayPoint.place === currentLocationLabel &&
    waypointInput.wayPoint?.place === currentLocationLabel
  ) {
    return 'var(--hds-accent)'
  }
}

RouteInputs.displayName = 'RouteInputs'
